/* eslint-disable react/prop-types */
import {
  Box,
  Card,
  CenteredLoader,
  ConnectedIcon,
  DisconnectedIcon,
  EmptyChart,
  SemiDonut,
  SemiDonutProps,
  Separator,
  SubTitle,
  Text,
  TooltipItem,
  useTheme,
} from "@shared/primitive-components"
import { useMemo } from "react"
import * as styles from "../styles/fleetListChartStyles"
import { FleetStats } from "../../../../api/fleet"

type ProtectedConnectivityStatsSemiDonutChartProps = {
  connected: number
  connectedPercentage: string
  disconnected: number
  disconnectedPercentage: string
}

const ProtectedConnectivityStatsSemiDonutChart = ({
  connected,
  connectedPercentage,
  disconnected,
  disconnectedPercentage,
}: ProtectedConnectivityStatsSemiDonutChartProps) => {
  const theme = useTheme()
  const chart = useMemo<SemiDonutProps<Number, String> | undefined>(() => {
    const data = [
      {
        label: `Connected`,
        value: connected,
        percentage: Number(connectedPercentage),
        color: theme.palette.full.green,
        icon: <ConnectedIcon />,
      },
      {
        label: `Disconnected`,
        value: disconnected,
        percentage: Number(disconnectedPercentage),
        color: theme.palette.full.red,
        icon: <DisconnectedIcon />,
      },
    ]

    const toolTipTitleFn = (tooltipItem: TooltipItem<"doughnut">[]): string => {
      const { dataIndex } = tooltipItem[0]
      return `${data[dataIndex].percentage}% of insured vehicles `
    }
    const toolTipLabelFn = (tooltipItem: TooltipItem<"doughnut">): string => {
      const { dataIndex, parsed } = tooltipItem
      return `${parsed} vehicles ${dataIndex === 0 ? `connected` : `disconnected`}`
    }

    return {
      data,
      toolTipLabelFn,
      toolTipTitleFn,
    }
  }, [
    connected,
    connectedPercentage,
    disconnected,
    disconnectedPercentage,
    theme.palette.full.green,
    theme.palette.full.red,
  ])

  if (!chart) return null

  return (
    <>
      <SemiDonut<Number, String>
        title="Connectivity status"
        subtitle="This is the connectivity status for all your vehicles currently insured with Flock. Make sure your vehicles are connected to unlock all the benefits of your policy."
        chart={chart}
        underChart={{ value: `${connectedPercentage}%`, label: `Connected` }}
      />
    </>
  )
}

type ConnectivityStatsSemiDonutChartProps = {
  stats: FleetStats | null
  outerLoading: boolean
}

export const ConnectivityStatsSemiDonutChart: React.FC<ConnectivityStatsSemiDonutChartProps> = ({
  stats,
  outerLoading,
}) => {
  if (outerLoading) {
    return (
      <Card css={styles.cardOuterContainer}>
        <Box css={styles.cardContainer}>
          <Box css={styles.marginBottom}>
            <SubTitle css={styles.title}>CONNECTIVITY STATUS</SubTitle>
            <Separator size="block" color="yellow" />
          </Box>
          <Box css={styles.loaderContainer}>
            <CenteredLoader />
          </Box>
        </Box>
      </Card>
    )
  }
  const text = `This is the connectivity status for all your vehicles currently insured with Flock. Make sure your vehicles are connected to unlock all the benefits of your policy.`
  if (!stats || stats.onCoverTotal === 0)
    return (
      <Card css={styles.cardOuterContainer}>
        <Box css={styles.cardContainer}>
          <Box css={styles.marginBottom}>
            <SubTitle css={styles.title}>CONNECTIVITY STATUS</SubTitle>
            <Separator size="block" color="yellow" />
          </Box>
          <Text css={styles.subtitle}>{text}</Text>
          <EmptyChart
            title="No data to show"
            description="Your policy does not yet have any on cover vehicles"
            showAxis={false}
          />
        </Box>
      </Card>
    )

  const connected = stats.connectedVrns.length
  const disconnected = Math.max(stats.onCoverTotal - connected, 0)
  const ratio = stats.connectivityRatio ?? 0
  const connectedPercentage = (ratio * 100).toFixed(0)
  const disconnectedPercentage = ((1 - ratio) * 100).toFixed(0)

  return (
    <ProtectedConnectivityStatsSemiDonutChart
      connected={connected}
      connectedPercentage={connectedPercentage}
      disconnected={disconnected}
      disconnectedPercentage={disconnectedPercentage}
    />
  )
}
